import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import { pick } from "../../../../../shared/lib/html5-software-library/src/ranj/util/RandomUtil";
import AbstractView from "../../../../../shared/src/ranj/equalture/view/AbstractView";
import PuzzleLocation from "../model/PuzzleLocation";
import PuzzleModel from "../model/PuzzleModel";
import PuzzleModelEvent from "../model/PuzzleModelEvent";
import PuzzleStatus from "../model/PuzzleStatus";
import ActorView from "./ActorView";
import BoatView from "./BoatView";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";

export default class PuzzleView extends AbstractView {

    /**
     * @param {createjs.Container} target 
     * @param {PuzzleModel} puzzleModel 
     */
    constructor(target, gameAssets, puzzleModel) {
        super(target);
        this._gameAssets = gameAssets;
        this._puzzleModel = puzzleModel;
        this._config = puzzleModel.config;

        this._handleModelMove = this._handleModelMove.bind(this);
        this._handleModelStatus = this._handleModelStatus.bind(this);
        this._puzzleModel.addEventListener(PuzzleModelEvent.MOVE, this._handleModelMove);
        this._puzzleModel.addEventListener(PuzzleModelEvent.STATUS, this._handleModelStatus);

        this.build();

        this.updateState(UpdateAnim.INTRO);

        this._enableInteraction(false);

        createjs.MotionGuidePlugin.install();

        //TEMP/TEST:
        if (ranj.DEBUG) {
            window.puzzleview = this;
        }
    }

    destroy() {
        super.destroy();
        for (var id in this._actorViews) {
            this._actorViews[id].destroy();
        }
        this._boat.destroy();

        this._puzzleModel.removeEventListener(PuzzleModelEvent.MOVE, this._handleModelMove);
        this._puzzleModel.removeEventListener(PuzzleModelEvent.STATUS, this._handleModelStatus);
        this._target.removeAllChildren();

        if (window.puzzleview == this) {
            window.puzzleview = null;
        }
    }

    get actorViews() {
        return this._actorViews;
    }

    build() {
        this._level = this._target.addChild(this._createAsset(this._config.skin));

        this._level.decoration.gotoAndStop(this._config.decoration);
        if (AppModel.easterEggsEnabled) {
            // this._level.decoration.gotoAndStop(pick(["euromast", "kabouter"]));
            this._level.decoration.gotoAndStop("kabouter");
        }

        this._boat = new BoatView(this._level.addChild(this._createAsset(this._config.boat.skin)));

        this._locationSlots = {};
        this._locationSlots[PuzzleLocation.BANK_FROM] = this._createSlots(this._level, "from");
        this._locationSlots[PuzzleLocation.BANK_TO] = this._createSlots(this._level, "to");
        this._locationSlots[PuzzleLocation.BOAT] = this._createSlots(this._boat.skin.container, "slot");

        //slots for the boat in the level (NOT for actors IN the boat)
        this._boatSlots = {};
        this._boatSlots[PuzzleLocation.BANK_FROM] = new Slot(this._level.boatFrom);
        this._boatSlots[PuzzleLocation.BANK_TO] = new Slot(this._level.boatTo);

        this._actorViews = {};
        this._config.actors.forEach(({ id, skin: skinId, fixed }) => {
            if (skinId) {
                this._actorViews[id] = this._createActor(skinId, fixed ? null : this._buildActorClickHandler(id));
            }
        });
        this._actorLocations = {};
        this._actorSlots = {};
    }

    updateState(animation = null) {
        const state = this._puzzleModel.getState();
        const oldSlots = this._getSlotsState();
        this.drawState(state);
        if (animation != null) {
            switch (animation) {
                case UpdateAnim.INTRO:
                    this._animateIn(oldSlots, this._getSlotsState());
                    break
                case UpdateAnim.MOVE:
                    this._doMoveAnimation(oldSlots, this._getSlotsState());
                    break
                case UpdateAnim.RESET:
                    this._doResetAnimation(oldSlots, this._getSlotsState());
                    break
                case UpdateAnim.NEW:
                    this._doNewAnimation(oldSlots, this._getSlotsState());
                    break
            }
        }
        this._enableInteraction(this._interactionEnabled);
    }

    drawState(state) {
        this._emptyAllSlots();
        this._positionBoat(state.boat);
        for (const [location, actors] of Object.entries(state.locations)) {
            actors.forEach(actor => {
                this._positionActor(actor, location);
            });
        }
    }

    //------------------------
    //     INTERACTION
    //------------------------


    _enableInteraction(value) {
        this._interactionEnabled = value;
        const state = this._puzzleModel.getState();
        const validLocations = [state.boat, PuzzleLocation.BOAT];
        for (let id in this._actorViews) {
            if (this._actorViews[id].button) {
                this._actorViews[id].button.setEnabled(value && validLocations.indexOf(this._actorLocations[id]) > -1);
            }
        }
    }

    _onActorClicked(actorId) {
        //actor in/out of boat
        this._actorViews[actorId].reset();//stops any animations
        this._puzzleModel.switchActorLocation(actorId);
    }

    _buildActorClickHandler(actorId) {
        return (e) => {
            this._onActorClicked(actorId);
        }
    }

    _handleBoatClicked(e) {
        this._puzzleModel.crossRiver();
    }


    //------------------------
    //     MODEL EVENTS
    //------------------------

    onMoveInvalid(actorId, location) {
        if (actorId) {
            //wiggle actor
            /**@type {ActorView} */
            const actorView = this._actorViews[actorId];
            actorView.wiggle();
        } else if (location == PuzzleLocation.BOAT) {
            this._boat.wiggle();
        }
    }

    _handleModelMove(e) {
        this.updateState(UpdateAnim.MOVE);
    }

    _handleModelStatus({ data: status }) {
        let interactive = false;
        switch (status) {
            case PuzzleStatus.STARTED:
                interactive = true;
                this.updateState(this._puzzleModel.failedAttemptCount > 0 ? UpdateAnim.RESET : null);
                break;
            case PuzzleStatus.TIMEOUT:
                interactive = true;
                break;
            case PuzzleStatus.FAILED:
                this._onFailed();
                this.updateState();
                break;
            case PuzzleStatus.COMPLETED:
                this.updateState();
        }
        this._enableInteraction(interactive);
    }

    _onFailed() {
        //blink involved actor(s)
        const { actors } = this._puzzleModel.failedConstraints[0];
        if (actors != null) { //can be null in debug situations (fake failure in level that cant fail)
            actors.forEach(actorId => {
                /**@type {ActorView} */
                const actorView = this._actorViews[actorId];
                actorView.blink();
            });
        }
    }

    //------------------------
    //     ANIMATION
    //------------------------

    _animateIn(fromSlots, toSlots) {
        //Hide actors, move boat+level in, show actors 1 by 1
        Object.values(toSlots.actors).forEach(slot => {
            slot.container.visible = false;
            slot.container.scaleX = slot.container.scaleY = .01;
        });
        const target = this._level;
        const origX = target.x, origY = target.y;
        target.x -= INTRO_OFFSET.x;
        target.y -= INTRO_OFFSET.y;
        createjs.Tween.get(target)
            .to({ x: origX, y: origY }, PuzzleView.ANIMATE_IN_DURATION, createjs.Ease.quadInOut)
            .call(() => {
                let delay = 100;
                const delta = 50;
                Object.values(toSlots.actors).forEach(slot => {
                    createjs.Tween.get(slot.container)
                        .wait(delay += delta)
                        .set({ visible: true })
                        .wait(1)
                        .to({ scaleX: 1, scaleY: 1 }, 1000, createjs.Ease.elasticOut);
                });
            });
    }

    animateOut() {
        const slots = this._actorSlots;
        //Animate out: move actors, boat + level out
        let delay = 150;
        const delta = 50;
        const target = this._level;
        const duration = 1000;
        createjs.Tween.get(target)
            .wait(delay)
            .to({ x: target.x + INTRO_OFFSET.x, y: target.y + INTRO_OFFSET.y }, duration, createjs.Ease.quadInOut)
            .call(() => {
                //Not really necessary because view will be destroyed after this..:
                Object.values(slots).forEach(slot => {
                    slot.container.visible = true;
                    slot.container.scaleX = slot.container.scaleY = 1;
                });
            });
        //return delay for initializing next view
        return delay;
    }

    _doMoveAnimation(fromSlots, toSlots) {
        let fromSlot = fromSlots.boat, slot = toSlots.boat;
        if (fromSlot != null && fromSlot != slot) {
            const from = fromSlot.container.localToLocal(0, 0, slot.container.parent);
            slot.container.x = from.x;
            slot.container.y = from.y;
            createjs.Tween.get(slot.container).to({ x: slot.origX, y: slot.origY }, 350, createjs.Ease.quadInOut);
        }
        Object.keys(toSlots.actors).forEach(actorId => {
            let fromSlot = fromSlots.actors[actorId], slot = toSlots.actors[actorId];
            if (fromSlot != null && fromSlot != slot) {
                const from = fromSlot.container.localToLocal(0, 0, slot.container.parent);
                slot.container.x = from.x;
                slot.container.y = from.y;
                const guide = { path: [from.x, from.y, (from.x + slot.origX) / 2, (from.y + slot.origY) / 2 - 300, slot.origX, slot.origY] };
                //createjs.Tween.get(slot.container).to({ x: slot.origX, y: slot.origY }, 200);
                createjs.Tween.get(slot.container).to({ guide: guide }, 100);
            }
        });
    }

    _doResetAnimation(fromSlots, toSlots) {
        //place actors at old slots, animate out (fade/pop)
        //place actors at new slots, animate in
        let delay = 0;
        const animate = (fromSlot, slot, dt = 0) => {
            const from = fromSlot.container.localToLocal(0, 0, slot.container.parent);
            slot.container.x = from.x;
            slot.container.y = from.y;
            createjs.Tween.get(slot.container)
                .wait(delay += dt)
                .to({ scaleX: .01, scaleY: .01 }, 150, createjs.Ease.quadIn)
                .set({ x: slot.origX, y: slot.origY })
                .wait(600)
                .to({ scaleX: 1, scaleY: 1 }, 300, createjs.Ease.elasticOut);
        };
        const actorIds = Object.keys(toSlots.actors);
        //First place boat on old location, so localToLocal works for actors on boat
        const fromSlot = fromSlots.boat, slot = toSlots.boat;
        if (fromSlots.boat != null && fromSlots.boat != toSlots.boat) {
            const from = fromSlot.container.localToLocal(0, 0, slot.container.parent);
            slot.container.x = from.x;
            slot.container.y = from.y;
            createjs.Tween.get(slot.container)
                .wait(delay + actorIds.length * 100 + 200)
                .to({ x: slot.origX, y: slot.origY }, 250, createjs.Ease.quadInOut);
        }

        actorIds.forEach(actorId => {
            let fromSlot = fromSlots.actors[actorId], slot = toSlots.actors[actorId];
            if (fromSlot != null) {//} && fromSlot != slot) {
                //if (fromSlot != null) {
                animate(fromSlot, slot, 100);
            }
        });

        if (fromSlots.boat != null && fromSlots.boat != toSlots.boat) {
            const from = fromSlot.container.localToLocal(0, 0, slot.container.parent);
            slot.container.x = from.x;
            slot.container.y = from.y;
            createjs.Tween.get(slot.container)
                .wait(delay + 200)
                .to({ x: slot.origX, y: slot.origY }, 250, createjs.Ease.quadInOut);
        }
    }

    _doNewAnimation(fromSlots, toSlots) {
    }


    //------------------------
    //     PLACEMENT
    //------------------------

    _getSlotsState() {
        return { boat: this._boatSlot, actors: Object.assign({}, this._actorSlots) };
    }

    _positionActor(actorId, location) {
        const preferredSlot = this._actorLocations[actorId] == location ? this._actorSlots[actorId] : null;
        const slot = this._getEmptySlot(location, preferredSlot);
        if (slot && this._actorViews[actorId]) {
            this._actorViews[actorId].reset();
            const actorSkin = this._actorViews[actorId].skin;
            this._actorLocations[actorId] = location;
            const fromSlot = this._actorSlots[actorId];
            this._actorSlots[actorId] = slot;
            this._positionItem(actorSkin, slot);
        }
    }

    _positionBoat(location) {
        const slot = this._boatSlots[location];
        const fromSlot = this._boatSlot;
        this._boatSlot = slot;
        this._positionItem(this._boat.skin, slot);
    }

    _positionItem(skin, slot) {
        slot.content = skin;
    }

    _getEmptySlot(location, preferredSlot) {
        if (preferredSlot && preferredSlot.isEmpty()) {
            return preferredSlot;
        }
        return this._locationSlots[location].find(slot => slot.isEmpty());
    }

    _emptyAllSlots() {
        for (let location in this._locationSlots) {
            this._locationSlots[location].forEach(slot => { slot.clear(); });
        }
    }


    //------------------------
    //     CREATION
    //------------------------

    _createActor(skinId, clickHandler = null) {
        const skin = this._createAsset(skinId, clickHandler);
        return new ActorView(skin);
    }

    _createAsset(skinId, clickHandler = null) {
        const skin = new this._gameAssets[skinId]();
        if (clickHandler != null) {
            skin.button = this.createButton(skin, clickHandler);
        }
        return skin;
    }

    _createSlots(parent, prefix) {
        const slots = [];
        for (let i = 0; i < 99; i++) {
            const slot = parent[prefix + i];
            if (slot) {
                slots.push(new Slot(slot));
            } else {
                break;
            }
        }
        return slots;
    }
};

const UpdateAnim = { INTRO: "INTRO", MOVE: "MOVE", RESET: "RESET", NEW: "NEW" };

const INTRO_OFFSET = { x: 1334, y: 773 };

PuzzleView.ANIMATE_IN_DURATION = 1000;

class Slot {
    constructor(container) {
        this.container = container;
        this.container.gotoAndStop(0);//populate .children
        this.origX = container.x;
        this.origY = container.y;
        this._content = null;
    }

    set content(actor) {
        this.container.removeAllChildren();
        this._content = actor;
        if (actor != null) {
            this.container.addChild(actor);
        }
    }

    get content() {
        return this._content;
    }

    clear() {
        this.content = null;
    }

    isEmpty() {
        return this._content == null;
    }

}